// Better‑than‑FAANG+ Media Constraints & Gates
// Keep original names (MAX_VIDEO_SIZE / MAX_IMAGE_SIZE) for backward compatibility
// while exposing richer tiered + gating constants for the upgraded mediaUpload.

/*
Design Goals:
1. Tiered soft/hard caps (free / verified / internal) to allow growth without code churn.
2. Distinct ingest vs delivery: we accept larger originals but force optimization downstream.
3. Defensive gates (dimensions, megapixels, duration, bitrate) to keep infra predictable.
4. Explicit GIF & RAW/HEIC allowances with conversion expectations (handled client / cloud).
5. Helpers to select active tier + produce an "effective limits" object.
*/

// ---------------- Base (Legacy Back-Compat) ----------------
export const MAX_IMAGE_SIZE = 1024 * 1024 * 50; // 20MB (legacy default)
export const MAX_VIDEO_SIZE = 1024 * 1024 * 512; // 90MB (legacy default)

// ---------------- Tiered Ingest Hard Caps ------------------
export const MEDIA_TIERS = {
  FREE: {
    IMAGE_HARD: 50 * 1024 * 1024, // 50MB
    IMAGE_SOFT: 20 * 1024 * 1024, // warn threshold
    VIDEO_HARD: 512 * 1024 * 1024, // 512MB
    VIDEO_SOFT: 200 * 1024 * 1024, // warn threshold
    GIF_HARD: 200 * 1024 * 1024, // before auto-convert to MP4/WebM
  },
  VERIFIED: {
    IMAGE_HARD: 60 * 1024 * 1024,
    IMAGE_SOFT: 30 * 1024 * 1024,
    VIDEO_HARD: 750 * 1024 * 1024,
    VIDEO_SOFT: 300 * 1024 * 1024,
    GIF_HARD: 250 * 1024 * 1024,
  },
  INTERNAL: {
    IMAGE_HARD: 100 * 1024 * 1024,
    IMAGE_SOFT: 40 * 1024 * 1024,
    VIDEO_HARD: 1536 * 1024 * 1024, // 1.5GB
    VIDEO_SOFT: 600 * 1024 * 1024,
    GIF_HARD: 300 * 1024 * 1024,
  },
};

// ---------------- Dimension & Megapixel Gates --------------
export const MAX_IMAGE_WIDTH = 8000;
export const MAX_IMAGE_HEIGHT = 8000;
export const MAX_IMAGE_MEGAPIXELS = 80; // Reject >80MP unless privileged
export const PRIVILEGED_MEGAPIXELS = 120; // Potential elevated gate (feature flag)

// ---------------- Video Structural Gates -------------------
export const MAX_VIDEO_DURATION_SEC = 20 * 60; // 20 minutes
export const MAX_VIDEO_BITRATE_MBPS = 60; // Gate absurd encodes
export const PRIVILEGED_VIDEO_DURATION_SEC = 45 * 60;

// ---------------- Thumbnail / Extraction Defaults ----------
export const VIDEO_THUMBNAIL_TIME_RATIOS = [0, 0.5]; // first frame + midpoint

// ---------------- Adaptive Ladder Preset -------------------
export const VIDEO_LADDER_PRESETS = [
  { w: 640, quality: 'q_auto:eco' },
  { w: 960, quality: 'q_auto:good' },
  { w: 1280, quality: 'q_auto:good' },
];

// ---------------- Transform Baselines ----------------------
export const BASE_IMAGE_TRANSFORM = 'q_auto,fl_progressive:steep';
export const BASE_VIDEO_TRANSFORM = 'f_auto,q_auto,vc_auto';

// ---------------- Fallback Formats -------------------------
export const FALLBACK_FORMATS = ['webp', 'jpg'];

// ---------------- GIF Optimization Policy ------------------
export const ENABLE_GIF_OPTIMIZATION = true;

// ---------------- Resumable / Chunk Strategy ---------------
export const DEFAULT_CHUNK_THRESHOLD_BYTES = 90 * 1024 * 1024; // Switch to chunk mode above 90MB
export const DEFAULT_CHUNK_SIZE_BYTES = 20 * 1024 * 1024;

// ---------------- Soft Warning Messages --------------------
export const SOFT_LIMIT_MESSAGE = {
  IMAGE:
    'Large image – will be optimized. Consider compressing for faster upload.',
  VIDEO:
    'Large video – chunked upload engaged. Consider trimming for faster processing.',
};

// ---------------- Helper: Select Active Tier ---------------
export function getTierLimits(tier = 'FREE') {
  const key = tier in MEDIA_TIERS ? tier : 'FREE';
  return { tier: key, ...MEDIA_TIERS[key] };
}

// ---------------- Helper: Effective Limits Object ----------
export function buildEffectiveLimits({
  tier = 'FREE',
  privileged = false,
} = {}) {
  const t = getTierLimits(tier);
  return {
    tier: t.tier,
    imageSoft: t.IMAGE_SOFT,
    imageHard: t.IMAGE_HARD,
    videoSoft: t.VIDEO_SOFT,
    videoHard: t.VIDEO_HARD,
    gifHard: t.GIF_HARD,
    maxImageMegapixels: privileged
      ? PRIVILEGED_MEGAPIXELS
      : MAX_IMAGE_MEGAPIXELS,
    maxImageWidth: MAX_IMAGE_WIDTH,
    maxImageHeight: MAX_IMAGE_HEIGHT,
    maxVideoDurationSec: privileged
      ? PRIVILEGED_VIDEO_DURATION_SEC
      : MAX_VIDEO_DURATION_SEC,
    maxVideoBitrateMbps: MAX_VIDEO_BITRATE_MBPS,
    chunkThreshold: DEFAULT_CHUNK_THRESHOLD_BYTES,
    chunkSize: DEFAULT_CHUNK_SIZE_BYTES,
  };
}

// ---------------- Backward Compatibility Export -------------
// For older code expecting single-value maxes, expose recommended defaults (FREE hard caps).
export const RECOMMENDED_MAX_IMAGE_BYTES = MEDIA_TIERS.FREE.IMAGE_HARD;
export const RECOMMENDED_MAX_VIDEO_BYTES = MEDIA_TIERS.FREE.VIDEO_HARD;

// Example dynamic selection (not executed here):
// const { imageHard, videoHard } = buildEffectiveLimits({ tier:
